// React hook for project management operations
// Implements project CRUD with authentication, logging, and error handling
// Persists all data in the store and exports only store data + mutation functions

import { useEffect, useMemo, useRef } from "react";
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { logger } from "@/services/logger";
import { generateUUID } from "@/utils/uuid";
import { useAppStore } from "@/stores/appStore";
import { transformEntitlements } from "@/utils/entitlements";
import { useEntitlementDebugStore } from "@/stores/useEntitlementDebugStore";
import {
  listTeams,
  listProjects,
  listWorkflows,
  createProject,
  updateProject,
  deleteProject,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
} from "@/services/api/sdk.gen";

import type {
  CreateProjectData,
  UpdateProjectData,
  DeleteProjectData,
  CreateWorkflowData,
  UpdateWorkflowData,
  DeleteWorkflowData,
  TeamListItem,
  ProjectListItem,
  WorkflowListItem,
  TeamListResponse,
  ProjectListResponse,
  WorkflowListResponse,
  ProjectResponse,
  WorkflowResponse,
} from "@/services/api/types.gen";
import type { Team, Project, Workflow } from "@/types";
import { workflowLockSessionKey } from "@/utils/lockSessionStorage";
import { useCollaborationOptional } from "@/contexts/CollaborationContext";

/**
 * The list / detail responses include a top-level `entitlements` payload
 * added by the auth middleware that isn't part of the OpenAPI schema.
 * Narrow access to that field with a thin extension type so we can read
 * it without `as any`.
 */
type WithEntitlements<T> = T & { entitlements?: unknown };

/**
 * The server returns thumbnailAssetId / thumbnailUrl on project list items,
 * but the generated SDK type doesn't model them yet. Use this locally
 * where we read those fields.
 */
type ProjectListItemWithThumbnail = ProjectListItem & {
  thumbnailAssetId?: string | null;
  thumbnailUrl?: string | null;
};

/**
 * Standard retry policy for the list queries in this hook: bail on auth
 * errors (401/403), otherwise retry up to 3 times.
 */
const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  if (status === 401 || status === 403) return false;
  return failureCount < 3;
};

// Default page size for pagination
const DEFAULT_PAGE_SIZE = 20;

/**
 * Options for useProjects hook
 */
interface UseProjectsOptions {
  /**
   * Whether to enable the workflows query. Defaults to false.
   * Set to true only on routes/components that need the workflows list.
   * This prevents unnecessary API calls that sign CloudFront URLs per-item,
   * which blocks the event loop. See issue #1303.
   */
  enableWorkflowsQuery?: boolean;
}

/**
 * Hook for project management operations - similar to useServices
 * Persists all queried data in the store and exports store data + mutations
 */
export const useProjects = (options?: UseProjectsOptions) => {
  const { enableWorkflowsQuery = false } = options ?? {};
  const { user } = useAuth();
  const {
    teams,
    projects,
    currentTeam,
    currentProject,
    workflows,
    setTeams,
    setProjects,
    setCurrentTeam,
    setCurrentProject,
    setWorkflows,
    setError,
    clearError,
    setEntitlements,
  } = useAppStore();

  const queryClient = useQueryClient();

  // Optional collaboration context — available only when inside CollaborationProvider.
  // Used to adopt locks returned by updateWorkflow so the frontend can track them.
  const collaborationContext = useCollaborationOptional();
  const setLock = collaborationContext?.setLock;

  // ============================================================================
  // TEAMS QUERY - Auto-fetch and persist in store
  // ============================================================================

  const teamsQuery = useQuery({
    queryKey: ["teams", user?.id],
    queryFn: async () => {
      const correlationId = generateUUID();

      logger.info("Fetching teams from backend", {
        userId: user?.id,
        userEmail: user?.email,
        correlationId,
      });

      try {
        clearError();

        const result = await listTeams({ throwOnError: true });
        const teamsData: TeamListItem[] =
          (result?.data as TeamListResponse | undefined)?.data ?? [];

        logger.info("Teams fetched successfully", {
          userId: user?.id,
          teamCount: teamsData.length,
          correlationId,
        });

        // Persist in store (Team[] from the app's store type;
        // structurally compatible with TeamListItem[]).
        setTeams(teamsData as unknown as Team[]);

        return teamsData;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to fetch teams", error as Error, {
          userId: user?.id,
          correlationId,
        });

        setError(`Failed to fetch teams: ${errorMessage}`);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: shouldRetryQuery,
  });

  // ============================================================================
  // PROJECTS QUERY - Fetch when team is selected
  // ============================================================================

  const projectsQuery = useQuery({
    queryKey: ["projects", user?.id],
    queryFn: async () => {
      const correlationId = generateUUID();

      logger.info("Fetching projects from backend", {
        userId: user?.id,
        correlationId,
      });

      try {
        clearError();

        const result = await listProjects({
          query: { limit: 100 }, // Defensive limit to prevent unbounded fetches
          throwOnError: true,
        });
        const responseBody = result?.data as
          | WithEntitlements<ProjectListResponse>
          | undefined;
        const projectsData: ProjectListItemWithThumbnail[] =
          (responseBody?.data as ProjectListItemWithThumbnail[]) ?? [];

        logger.info("Projects fetched successfully", {
          userId: user?.id,
          projectCount: projectsData.length,
          correlationId,
        });

        // Debug: Log thumbnail data for each project
        projectsData.forEach((p) => {
          if (p.thumbnailAssetId || p.thumbnailUrl) {
            logger.debug("[useProjects] Project thumbnail data", {
              projectId: p.id,
              projectName: p.name,
              thumbnailAssetId: p.thumbnailAssetId,
              thumbnailUrl: p.thumbnailUrl,
            });
          }
        });

        // Persist in store (Project[] from the app's store type;
        // structurally compatible with ProjectListItem[]).
        setProjects(projectsData as unknown as Project[]);

        // Capture root-level entitlements from response (Stage 11)
        // API returns array-based entitlements (D34) — transform to O(1) lookup maps
        const rawEntitlements = responseBody?.entitlements ?? null;
        if (rawEntitlements) {
          if (import.meta.env.DEV) {
            useEntitlementDebugStore
              .getState()
              .log(
                "set",
                "useProjects.projectsQuery",
                `Extracted entitlements from listProjects response (${projectsData.length} projects)`,
                undefined,
                rawEntitlements,
              );
          }
          const mapped = transformEntitlements(rawEntitlements);
          if (mapped) {
            setEntitlements(mapped, "useProjects → listProjects");
          }
        }

        return projectsData;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to fetch projects", error as Error, {
          userId: user?.id,
          correlationId,
        });

        setError(`Failed to fetch projects: ${errorMessage}`);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: shouldRetryQuery,
  });

  // ============================================================================
  // WORKFLOWS QUERY - Fetch when project is selected
  // ============================================================================

  const workflowsQuery = useQuery({
    queryKey: ["workflows", currentProject?.id, user?.id],
    queryFn: async () => {
      const correlationId = generateUUID();

      if (!currentProject?.id) {
        throw new Error("Project ID is required to fetch workflows");
      }

      const projectIdAtStart = currentProject.id;

      logger.info("Fetching workflows for project", {
        userId: user?.id,
        projectId: projectIdAtStart,
        correlationId,
      });

      try {
        clearError();

        const result = await listWorkflows({
          path: { projectId: projectIdAtStart },
          query: { limit: 100 }, // Defensive limit to prevent unbounded fetches
          throwOnError: true,
        });
        const responseBody = result?.data as
          | WithEntitlements<WorkflowListResponse>
          | undefined;
        const workflowsData: WorkflowListItem[] = responseBody?.data ?? [];

        // Capture entitlements from workflow list response
        const rawEntitlements = responseBody?.entitlements ?? null;
        if (rawEntitlements) {
          if (import.meta.env.DEV) {
            useEntitlementDebugStore
              .getState()
              .log(
                "set",
                "useProjects.workflowsQuery",
                `Extracted entitlements from listWorkflows response (project: ${projectIdAtStart})`,
                undefined,
                rawEntitlements,
              );
          }
          const mapped = transformEntitlements(rawEntitlements);
          if (mapped) {
            setEntitlements(
              mapped,
              `useProjects → listWorkflows (project: ${projectIdAtStart})`,
            );
          }
        }

        logger.info("Workflows fetched successfully", {
          userId: user?.id,
          projectId: projectIdAtStart,
          workflowCount: workflowsData.length,
          correlationId,
        });

        // Store sync is handled by the useEffect watching workflowsQuery.data
        // This ensures both cache hits and fresh fetches update the store

        return workflowsData;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to fetch workflows", error as Error, {
          userId: user?.id,
          projectId: currentProject?.id,
          correlationId,
        });

        setError(`Failed to fetch workflows: ${errorMessage}`);
        throw error;
      }
    },
    // Only fetch when explicitly enabled AND user/project are available.
    // This prevents eager fetching on all protected routes. See #1303.
    enabled: enableWorkflowsQuery && !!user && !!currentProject?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes (match global default)
    // Disable automatic refetch on window focus to prevent idle polling.
    // Workflows list doesn't need real-time freshness. See #1303.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: shouldRetryQuery,
  });

  // ============================================================================
  // SYNC TQ CACHE → ZUSTAND STORE
  // When switching back to a previously-visited project, TanStack Query
  // returns cached data without re-running queryFn. This effect ensures
  // the Zustand store stays in sync with TQ's data for the current project.
  // ============================================================================

  useEffect(() => {
    if (workflowsQuery.data && currentProject?.id) {
      setWorkflows(workflowsQuery.data as unknown as Workflow[]);
    } else if (!currentProject?.id) {
      setWorkflows([]);
    }
  }, [workflowsQuery.data, currentProject?.id, setWorkflows]);

  // ============================================================================
  // PROJECT MUTATIONS
  // ============================================================================

  const createProjectMutation = useMutation({
    mutationFn: async (data: CreateProjectData) => {
      const correlationId = generateUUID();

      logger.info("Creating new project", {
        userId: user?.id,
        projectName: data.body?.name,
        correlationId,
      });

      try {
        clearError();

        const result = await createProject(data);
        const created = (result?.data as ProjectResponse | undefined)?.data;

        logger.info("Project created successfully", {
          userId: user?.id,
          projectId: created?.id,
          projectName: created?.name,
          correlationId,
        });

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to create project", error as Error, {
          userId: user?.id,
          projectName: data.body?.name,
          correlationId,
        });

        setError(`Failed to create project: ${errorMessage}`);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: async (data: UpdateProjectData) => {
      const correlationId = generateUUID();

      logger.info("Updating project", {
        userId: user?.id,
        projectId: data.path?.projectId,
        body: data.body,
        correlationId,
      });

      try {
        clearError();

        const result = await updateProject(data);
        const updated = (result?.data as ProjectResponse | undefined)?.data;

        logger.info("Project updated successfully", {
          userId: user?.id,
          projectId: updated?.id,
          resultData: result?.data,
          correlationId,
        });

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to update project", error as Error, {
          userId: user?.id,
          projectId: data.path?.projectId,
          correlationId,
        });

        setError(`Failed to update project: ${errorMessage}`);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: async (data: DeleteProjectData) => {
      const correlationId = generateUUID();

      logger.info("Deleting project", {
        userId: user?.id,
        projectId: data.path?.projectId,
        correlationId,
      });

      try {
        clearError();

        const result = await deleteProject(data);

        logger.info("Project deleted successfully", {
          userId: user?.id,
          projectId: data.path?.projectId,
          correlationId,
        });

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to delete project", error as Error, {
          userId: user?.id,
          projectId: data.path?.projectId,
          correlationId,
        });

        setError(`Failed to delete project: ${errorMessage}`);
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });

      // Clear current project if it was deleted
      if (currentProject?.id === variables.path?.projectId) {
        setCurrentProject(null);
      }
    },
  });

  // ============================================================================
  // WORKFLOW MUTATIONS
  // ============================================================================

  const createWorkflowMutation = useMutation({
    mutationFn: async (data: CreateWorkflowData) => {
      const correlationId = generateUUID();

      logger.info("Creating new workflow", {
        userId: user?.id,
        workflowName: data.body?.name,
        projectId: data.path?.projectId,
        correlationId,
      });

      try {
        clearError();

        const result = await createWorkflow(data);
        const created = (result?.data as WorkflowResponse | undefined)?.data;

        logger.info("Workflow created successfully", {
          userId: user?.id,
          workflowId: created?.id,
          workflowName: created?.name,
          correlationId,
        });

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to create workflow", error as Error, {
          userId: user?.id,
          workflowName: data.body?.name,
          correlationId,
        });

        setError(`Failed to create workflow: ${errorMessage}`);
        throw error;
      }
    },
    onSuccess: () => {
      if (import.meta.env.DEV) {
        useEntitlementDebugStore
          .getState()
          .log(
            "mutation",
            "useProjects.createWorkflow",
            "Workflow created. Invalidating workflows + projects (entitlement counters).",
          );
      }
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] }); // Refresh entitlement counters
    },
  });

  const updateWorkflowMutation = useMutation({
    mutationFn: async (data: UpdateWorkflowData) => {
      const correlationId = generateUUID();
      const workflowId = data.path?.workflowId;

      logger.info("Updating workflow", {
        userId: user?.id,
        workflowId,
        correlationId,
      });

      try {
        clearError();

        // Include lockId if available from sessionStorage (prevents orphan locks).
        // The lock may have been acquired by the editor or a previous update.
        const savedLockId = workflowId
          ? sessionStorage.getItem(workflowLockSessionKey(workflowId))
          : null;

        const result = await updateWorkflow({
          ...data,
          body: {
            ...data.body,
            lockId: data.body?.lockId ?? savedLockId ?? undefined,
          },
        });
        const updated = (result?.data as WorkflowResponse | undefined)?.data;

        logger.info("Workflow updated successfully", {
          userId: user?.id,
          workflowId: updated?.id,
          correlationId,
        });

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to update workflow", error as Error, {
          userId: user?.id,
          workflowId,
          correlationId,
        });

        setError(`Failed to update workflow: ${errorMessage}`);
        throw error;
      }
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });

      // Adopt any lock returned by the API (auto-acquired or refreshed).
      // This prevents orphan locks that block subsequent updates (see issue #947).
      const workflowId = variables.path?.workflowId;
      const responseData = result?.data as
        | {
            meta?: {
              workflowLock?: {
                lockId?: string;
                lockedBy?: string;
                lockedAt?: string;
                lockExpiresAt?: string;
                refreshIntervalSeconds?: number;
              };
            };
          }
        | undefined;
      const lockData = responseData?.meta?.workflowLock;

      if (lockData?.lockId && workflowId) {
        logger.info(
          "[Collaboration] Adopting lock from updateWorkflow response (useProjects)",
          {
            workflowId,
            lockId: lockData.lockId,
            lockExpiresAt: lockData.lockExpiresAt,
          },
        );
        sessionStorage.setItem(
          workflowLockSessionKey(workflowId),
          lockData.lockId,
        );

        // If inside CollaborationProvider, also update the context state
        if (setLock) {
          setLock({
            workflowId,
            isLocked: true,
            isOwner: true,
            lockId: lockData.lockId,
            lockedBy: lockData.lockedBy,
            lockedAt: lockData.lockedAt,
            lockExpiresAt: lockData.lockExpiresAt,
            refreshIntervalSeconds: lockData.refreshIntervalSeconds ?? 60,
          });
        }
      }
    },
  });

  const deleteWorkflowMutation = useMutation({
    mutationFn: async (data: DeleteWorkflowData) => {
      const correlationId = generateUUID();

      logger.info("Deleting workflow", {
        userId: user?.id,
        workflowId: data.path?.workflowId,
        correlationId,
      });

      try {
        clearError();

        const result = await deleteWorkflow(data);

        logger.info("Workflow deleted successfully", {
          userId: user?.id,
          workflowId: data.path?.workflowId,
          correlationId,
        });

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to delete workflow", error as Error, {
          userId: user?.id,
          workflowId: data.path?.workflowId,
          correlationId,
        });

        setError(`Failed to delete workflow: ${errorMessage}`);
        throw error;
      }
    },
    onSuccess: () => {
      if (import.meta.env.DEV) {
        useEntitlementDebugStore
          .getState()
          .log(
            "mutation",
            "useProjects.deleteWorkflow",
            "Workflow deleted. Invalidating workflows + projects (entitlement counters).",
          );
      }
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] }); // Refresh entitlement counters
    },
  });

  // ============================================================================
  // HELPER FUNCTIONS FOR TEAM/PROJECT SELECTION
  // ============================================================================

  const selectTeam = (teamId: string) => {
    const team = teams.find((t) => t.id === teamId);
    if (team) {
      setCurrentTeam(team);
      setCurrentProject(null); // Clear project when team changes
      setProjects([]); // Clear projects list
      setWorkflows([]); // Clear workflows list

      logger.info("Team selected", {
        teamId,
        teamName: team.name,
        userId: user?.id,
        correlationId: generateUUID(),
      });
    }
  };

  const selectProject = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (project) {
      // Cancel any in-flight workflow queries to prevent stale data
      // from overwriting the store after we switch projects
      queryClient.cancelQueries({ queryKey: ["workflows"] });

      // Invalidate cached workflows so TanStack Query refetches
      queryClient.invalidateQueries({
        queryKey: ["workflows", projectId, user?.id],
      });

      setCurrentProject(project);
      setWorkflows([]); // Clear store while fresh fetch happens

      logger.info("Project selected", {
        projectId,
        projectName: project.name,
        userId: user?.id,
        correlationId: generateUUID(),
      });
    }
  };

  // ============================================================================
  // API WRAPPER FUNCTIONS
  // ============================================================================

  const createProjectWrapper = async (name: string, description?: string) => {
    return createProjectMutation.mutateAsync({
      body: { name, description },
      url: "/v1/projects",
    });
  };

  const updateProjectWrapper = async (
    projectId: string,
    updates: {
      name?: string;
      description?: string | null;
      thumbnailAssetId?: string | null;
    },
  ) => {
    return updateProjectMutation.mutateAsync({
      path: { projectId },
      // Wrapper accepts a slightly richer body than UpdateProjectData
      // (nullable description, optional thumbnailAssetId) because the
      // server accepts those fields even though the generated SDK type
      // doesn't yet model them. Cast the body specifically rather than
      // the whole envelope.
      body: updates as UpdateProjectData["body"],
      url: "/v1/projects/{projectId}",
    });
  };

  const deleteProjectWrapper = async (projectId: string) => {
    return deleteProjectMutation.mutateAsync({
      path: { projectId },
      url: "/v1/projects/{projectId}",
    });
  };

  const createEmptyWorkflowWrapper = async (
    projectId: string,
    name: string,
    description?: string,
    workflowData?: CreateWorkflowData["body"]["workflowData"],
  ) => {
    return createWorkflowMutation.mutateAsync({
      path: { projectId },
      body: {
        name,
        description,
        workflowData: workflowData || {
          nodes: [],
          edges: [],
          viewport: { x: 0, y: 0, zoom: 1 },
        },
      },
      url: "/v1/projects/{projectId}/workflows",
    });
  };

  const updateWorkflowWrapper = async (
    workflowId: string,
    updates: {
      name?: string;
      description?: string;
      thumbnailAssetId?: string | null;
      workflowData?: UpdateWorkflowData["body"]["workflowData"];
    },
  ) => {
    return updateWorkflowMutation.mutateAsync({
      path: { workflowId },
      body: updates as UpdateWorkflowData["body"],
      url: "/v1/workflows/{workflowId}",
    });
  };

  const deleteWorkflowWrapper = async (workflowId: string) => {
    return deleteWorkflowMutation.mutateAsync({
      path: { workflowId },
      url: "/v1/workflows/{workflowId}",
    });
  };

  // ============================================================================
  // RETURN STORE DATA + MUTATIONS (like useServices)
  // ============================================================================

  return {
    // Store data
    teams,
    projects,
    currentTeam,
    currentProject,
    workflows,

    // Loading states
    isLoadingTeams: teamsQuery.isLoading,
    isLoadingProjects: projectsQuery.isLoading,
    isLoadingWorkflows: workflowsQuery.isLoading,

    // API-agnostic mutation functions
    createProject: createProjectWrapper,
    updateProject: updateProjectWrapper,
    deleteProject: deleteProjectWrapper,
    createEmptyWorkflow: createEmptyWorkflowWrapper,
    //todo:createWorkflowFromTemplate
    updateWorkflow: updateWorkflowWrapper,
    deleteWorkflow: deleteWorkflowWrapper,

    // Mutation states
    isCreatingProject: createProjectMutation.isPending,
    isUpdatingProject: updateProjectMutation.isPending,
    isDeletingProject: deleteProjectMutation.isPending,
    isCreatingWorkflow: createWorkflowMutation.isPending,
    isUpdatingWorkflow: updateWorkflowMutation.isPending,
    isDeletingWorkflow: deleteWorkflowMutation.isPending,

    // Helper functions
    selectTeam,
    selectProject,

    // Refetch functions
    refetchTeams: teamsQuery.refetch,
    refetchProjects: projectsQuery.refetch,
    refetchWorkflows: workflowsQuery.refetch,
  };
};

// ============================================================================
// INFINITE QUERY HOOKS FOR PAGINATION
// ============================================================================

interface InfiniteProjectsOptions {
  limit?: number;
}

/**
 * Hook for fetching projects with infinite scroll pagination.
 * Returns paginated data with fetchNextPage for infinite scroll UI.
 */
export const useInfiniteProjects = (options: InfiniteProjectsOptions = {}) => {
  const { limit = DEFAULT_PAGE_SIZE } = options;
  const { user } = useAuth();
  const { setProjects, setEntitlements, setError, clearError } = useAppStore();

  const query = useInfiniteQuery({
    queryKey: ["projects", "infinite", user?.id, limit],
    queryFn: async ({ pageParam = 1 }) => {
      const correlationId = generateUUID();

      logger.info("Fetching projects page", {
        userId: user?.id,
        page: pageParam,
        limit,
        correlationId,
      });

      try {
        clearError();

        const result = await listProjects({
          query: { page: pageParam, limit },
          throwOnError: true,
        });

        const responseData = result?.data as
          | WithEntitlements<ProjectListResponse>
          | undefined;
        const projectsData: ProjectListItem[] = responseData?.data ?? [];
        const meta = (responseData?.meta ?? {}) as {
          total?: number;
          hasMore?: boolean;
        };

        logger.info("Projects page fetched successfully", {
          userId: user?.id,
          page: pageParam,
          projectCount: projectsData.length,
          hasMore: meta.hasMore,
          correlationId,
        });

        // Capture entitlements from first page
        if (pageParam === 1) {
          const rawEntitlements = responseData?.entitlements ?? null;
          if (rawEntitlements) {
            if (import.meta.env.DEV) {
              useEntitlementDebugStore
                .getState()
                .log(
                  "set",
                  "useInfiniteProjects",
                  `Extracted entitlements from listProjects response`,
                  undefined,
                  rawEntitlements,
                );
            }
            const mapped = transformEntitlements(rawEntitlements);
            if (mapped) {
              setEntitlements(mapped, "useInfiniteProjects → listProjects");
            }
          }
        }

        return {
          data: projectsData,
          meta: {
            page: pageParam,
            limit,
            total: meta.total || 0,
            hasMore: meta.hasMore ?? projectsData.length >= limit,
          },
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to fetch projects page", error as Error, {
          userId: user?.id,
          page: pageParam,
          correlationId,
        });

        setError(`Failed to fetch projects: ${errorMessage}`);
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.meta.hasMore ? lastPage.meta.page + 1 : undefined;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: shouldRetryQuery,
  });

  // Flatten all pages into a single array (memoized to prevent infinite loops).
  // Cast to the app's Project type at the boundary; the SDK's ProjectListItem
  // is a structural subset and the server populates the extra fields the
  // app type expects at runtime.
  const projects = useMemo(
    () =>
      (query.data?.pages.flatMap((page) => page.data) ??
        []) as unknown as Project[],
    [query.data],
  );

  // Get total from the first page's meta
  const total = query.data?.pages[0]?.meta.total ?? 0;

  // Track previous data to avoid unnecessary store updates
  const prevDataRef = useRef(query.data);

  // Sync with store when data changes (use query.data as stable reference)
  useEffect(() => {
    // Only update store if data has actually changed
    if (query.data && query.data !== prevDataRef.current) {
      prevDataRef.current = query.data;
      const flattenedProjects = query.data.pages.flatMap((page) => page.data);
      if (flattenedProjects.length > 0) {
        setProjects(flattenedProjects as unknown as Project[]);
      }
    }
  }, [query.data, setProjects]);

  return {
    projects,
    total,
    isLoading: query.isLoading,
    // True once first page has loaded (data !== undefined)
    // Use this to differentiate "loading" from "empty result"
    isInitialized: query.data !== undefined,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

interface InfiniteWorkflowsOptions {
  projectId: string | null | undefined;
  limit?: number;
}

/**
 * Hook for fetching workflows with infinite scroll pagination.
 * Returns paginated data with fetchNextPage for infinite scroll UI.
 */
export const useInfiniteWorkflows = (options: InfiniteWorkflowsOptions) => {
  const { projectId, limit = DEFAULT_PAGE_SIZE } = options;
  const { user } = useAuth();
  const { setWorkflows, setEntitlements, setError, clearError } = useAppStore();

  const query = useInfiniteQuery({
    queryKey: ["workflows", "infinite", projectId, user?.id, limit],
    queryFn: async ({ pageParam = 1 }) => {
      if (!projectId) {
        throw new Error("Project ID is required to fetch workflows");
      }

      const correlationId = generateUUID();

      logger.info("Fetching workflows page", {
        userId: user?.id,
        projectId,
        page: pageParam,
        limit,
        correlationId,
      });

      try {
        clearError();

        const result = await listWorkflows({
          path: { projectId },
          query: { page: pageParam, limit },
          throwOnError: true,
        });

        const responseData = result?.data as
          | WithEntitlements<WorkflowListResponse>
          | undefined;
        const workflowsData: WorkflowListItem[] = responseData?.data ?? [];
        const meta = (responseData?.meta ?? {}) as {
          total?: number;
          hasMore?: boolean;
        };

        // Capture entitlements from first page
        if (pageParam === 1) {
          const rawEntitlements = responseData?.entitlements ?? null;
          if (rawEntitlements) {
            if (import.meta.env.DEV) {
              useEntitlementDebugStore
                .getState()
                .log(
                  "set",
                  "useInfiniteWorkflows",
                  `Extracted entitlements from listWorkflows response (project: ${projectId})`,
                  undefined,
                  rawEntitlements,
                );
            }
            const mapped = transformEntitlements(rawEntitlements);
            if (mapped) {
              setEntitlements(
                mapped,
                `useInfiniteWorkflows → listWorkflows (project: ${projectId})`,
              );
            }
          }
        }

        logger.info("Workflows page fetched successfully", {
          userId: user?.id,
          projectId,
          page: pageParam,
          workflowCount: workflowsData.length,
          hasMore: meta.hasMore,
          correlationId,
        });

        return {
          data: workflowsData,
          meta: {
            page: pageParam,
            limit,
            total: meta.total || 0,
            hasMore: meta.hasMore ?? workflowsData.length >= limit,
          },
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to fetch workflows page", error as Error, {
          userId: user?.id,
          projectId,
          page: pageParam,
          correlationId,
        });

        setError(`Failed to fetch workflows: ${errorMessage}`);
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.meta.hasMore ? lastPage.meta.page + 1 : undefined;
    },
    enabled: !!user && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes (match global default)
    // Disable automatic refetch on window focus to prevent idle polling.
    // The workflows list doesn't need real-time freshness. See #1303.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: shouldRetryQuery,
  });

  // Flatten all pages into a single array (memoized to prevent infinite loops).
  // Cast to the app's Workflow type at the boundary; the SDK's
  // WorkflowListItem is a structural subset and the server populates the
  // extra fields the app type expects at runtime.
  const workflows = useMemo(
    () =>
      (query.data?.pages.flatMap((page) => page.data) ??
        []) as unknown as Workflow[],
    [query.data],
  );

  // Get total from the first page's meta
  const total = query.data?.pages[0]?.meta.total ?? 0;

  // Track previous data to avoid unnecessary store updates
  const prevDataRef = useRef(query.data);

  // Sync with store when data changes (use query.data as stable reference)
  useEffect(() => {
    // Only update store if data has actually changed
    if (projectId && query.data && query.data !== prevDataRef.current) {
      prevDataRef.current = query.data;
      const flattenedWorkflows = query.data.pages.flatMap((page) => page.data);
      setWorkflows(flattenedWorkflows as unknown as Workflow[]);
    }
  }, [query.data, projectId, setWorkflows]);

  return {
    workflows,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
